import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Text16, Title2 } from '@sorare/core/src/atoms/typography';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { Avatar_publicUserInfoInterface } from '@sorare/core/src/components/user/Avatar/__generated__/index.graphql';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import {
  MLB,
  MLB_LEAGUES_JOIN_LEAGUESLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { useTypedParams } from '@sorare/core/src/lib/routing';

import { LeagueMembers } from '@sorare/us-sports/src/components/leagues/LeagueMembers';

import {
  BaseballLeaguesSlugQuery,
  BaseballLeaguesSlugQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const MLB_LEAGUES_SLUG_QUERY = gql`
  query BaseballLeaguesSlugQuery($leagueSlug: String!, $offset: Int) {
    league(slug: $leagueSlug) {
      slug
      name
      affiliate {
        id
        slug
        nickname
        suspended
        avatarUrl
      }
      ...LeagueMembers_League
    }
    currentUser {
      slug
      leagues(sport: BASEBALL) {
        slug
      }
    }
  }
  ${LeagueMembers.fragments.League}
` as TypedDocumentNode<
  BaseballLeaguesSlugQuery,
  BaseballLeaguesSlugQueryVariables
>;

const Wrapper = styled.div`
  margin: var(--quadruple-unit);
`;

const Header = styled.header`
  padding: var(--double-unit) 0;
`;

const AffiliateLink = styled(MyClubLink)`
  display: inline-flex;
  gap: var(--unit);
  align-items: center;
`;

const ContainerWithGap = styled(Container)`
  gap: var(--double-unit) 0;
  margin: 0 0 var(--quadruple-unit);
`;

const LeaguesSlugPage = () => {
  const { leagueSlug } = useTypedParams<PageParams>();

  const { data, loading, fetchMore } = useQuery(MLB_LEAGUES_SLUG_QUERY, {
    variables: {
      leagueSlug,
      offset: 0,
    },
    skip: !leagueSlug,
  });

  const league = data?.league;

  if (loading || !league) {
    return (
      <Wrapper>
        <LoadingIndicator fullHeight />
      </Wrapper>
    );
  }

  return (
    <ContainerWithGap>
      <Header>
        <Title2 as="h1">{league.name}</Title2>
        {league.affiliate && (
          <AffiliateLink user={league.affiliate} sport={Sport.BASEBALL}>
            <Avatar
              user={
                {
                  ...league.affiliate,
                  pictureUrl: league.affiliate.avatarUrl,
                } as Avatar_publicUserInfoInterface
              }
            />
            <Text16>{league.affiliate?.nickname}</Text16>
          </AffiliateLink>
        )}
      </Header>
      <LeagueMembers
        league={league}
        sport={Sport.BASEBALL}
        afterLeaveRoute={MLB}
        inviteRoute={MLB_LEAGUES_JOIN_LEAGUESLUG}
        isMember={
          data.currentUser?.leagues.map(l => l.slug).includes(league.slug) ??
          false
        }
        loadMore={() => {
          fetchMore({
            variables: {
              offset: data.league?.members.length,
            },
          });
        }}
      />
    </ContainerWithGap>
  );
};

export default LeaguesSlugPage;
